import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SVGICON } from "../../constant/theme";

import SocialNetworkRadialChart from "./elements/SocialNetworkRadialChart";
import ProjectOverviewTab from "./elements/ProjectOverviewTab";
import ActiveProjects from "./elements/ActiveProjects";
import RevenueHistoryTable from "./elements/RevenueHistoryTable";

import {
  fetchDashboardSummaryThunk,
  resetDashboard,
} from "../../../store/slices/dashboardSlice";
import { fetchTenantTreeThunk } from "../../../store/slices/userPanchayathAssignSlice";

const toNumArray = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .map(Number)
    .filter((n) => Number.isFinite(n) && n > 0);

const toNumber = (val) => {
  const n = Number(val);
  return Number.isFinite(n) ? n : 0;
};

const formatCount = (val) => {
  const n = toNumber(val);
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 0,
  }).format(n);
};

const formatCurrency = (val, digits = 2) => {
  const n = toNumber(val);
  return `₹${new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  }).format(n)}`;
};

const formatLiters = (val) => {
  const n = toNumber(val);

  if (!Number.isFinite(n) || n <= 0) return "0 Ltrs";

  const clean = (num) =>
    String(num.toFixed(2))
      .replace(/\.00$/, "")
      .replace(/(\.\d*[1-9])0$/, "$1");

  if (n >= 10000000) {
    return `${clean(n / 10000000)} Cr Ltrs`;
  }

  if (n >= 100000) {
    return `${clean(n / 100000)} Lakh Ltrs`;
  }

  if (n >= 1000) {
    return `${clean(n / 1000)} K Ltrs`;
  }

  return `${new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n)} Ltrs`;
};

const Dashboard3 = () => {
  const dispatch = useDispatch();

  const { data, loading, error } = useSelector((s) => s.dashboard);
  const tenantTree = useSelector((s) => s.userPanAssign?.tenantTree || {});

  const role = String(useSelector((s) => s.auth?.user?.role) || "").toLowerCase();
  const isSuperadmin = role === "superadmin";

  const panId = Number(useSelector((s) => s.tenant?.pan_id) || 0);
  const panIds = toNumArray(useSelector((s) => s.tenant?.pan_ids || []));

  const [viewType, setViewType] = useState("water");

  const tenantReady = useMemo(() => {
    if (!isSuperadmin) return true;
    return panIds.length > 0 || panId > 0;
  }, [isSuperadmin, panIds, panId]);

  const panScopeKey = useMemo(() => {
    const ids = panIds.length ? panIds.join(",") : "";
    const id = panId > 0 ? String(panId) : "";
    return ids || id;
  }, [panIds, panId]);

  useEffect(() => {
    if (!tenantReady || !panScopeKey) return;

    dispatch(resetDashboard());
    const p = dispatch(fetchDashboardSummaryThunk());
    return () => p?.abort && p.abort();
  }, [dispatch, tenantReady, panScopeKey]);

  useEffect(() => {
    dispatch(fetchTenantTreeThunk());
  }, [dispatch]);

  const c = data?.cards || {};
  const receipts = data?.todayReceipts || [];
  const waterBills = data?.waterBills || [];
  const slrmBills = data?.slrmBills || [];

  const totalDistricts = tenantTree.districts?.length || 0;
  const totalTaluks = tenantTree.taluks?.length || 0;
  const totalPanchayaths = tenantTree.panchayaths?.length || 0;


  const showCount = (v) => (loading ? "..." : formatCount(v));
  const showCurrency = (v, digits = 2) =>
    loading ? "..." : formatCurrency(v, digits);
  const showLiters = (v) => (loading ? "..." : formatLiters(v));

  const topCards = [
    {
      svg: SVGICON.TotalDistricts || SVGICON.Wards,
      number: showCount(totalDistricts),
      title: "Total Districts",
      changetheme: "primary",
    },
    {
      svg: SVGICON.TotalTaluks || SVGICON.Taluks || SVGICON.List,
      number: showCount(totalTaluks),
      title: "Total Taluks",
      changetheme: "warning",
    },
    {
      svg:
        SVGICON.TotalPanchayaths ||
        SVGICON.ManagePanchayath ||
        SVGICON.HouseMaster,
      number: showCount(totalPanchayaths),
      title: "Total Panchayaths",
      changetheme: "success",
    },
    {
      svg: SVGICON.TotalHouses || SVGICON.UserSvgGreen,
      number: showCount(c.totalHouses ?? 0),
      title: "Total Houses",
      changetheme: "info",
    },
  ];

  const waterCards = [
    {
      svg: SVGICON.PipeConnection || SVGICON.UserSvgGreen,
      number: showCount(c.totalWaterConnections ?? 0),
      title: "Total Water Connections",
      changetheme: "success",
    },
    {
      svg: SVGICON.WaterDrop || SVGICON.CardProgress,
      number: showCurrency(c.totalBalanceWater ?? 0),
      title: "Total Water Balance",
      changetheme: "primary",
    },
    {
      svg: SVGICON.HouseCoverage || SVGICON.ShiledPrimary,
      number: showCount(c.housesCoveredThisMonth ?? 0),
      title: "Houses Covered - This Month",
      changetheme: "purple",
    },
    {
      svg: SVGICON.WaterUsage || SVGICON.UserThree,
      number: showLiters(c.waterUsageLiters ?? 0),
      title: "Water Usage - This Month",
      changetheme: "danger",
    },
    {
      svg: SVGICON.DemandGauge || SVGICON.ShiledPrimary,
      number: showCurrency(c.totalDemandThisMonth ?? 0),
      title: "Water Demand - This Month",
      changetheme: "warning",
    },
    {
      svg: SVGICON.CollectedAmount || SVGICON.CardProgress,
      number: showCurrency(c.waterCollectedThisMonth ?? 0),
      title: "Water Collections - This Month",
      changetheme: "success",
    },
  ];

  const slrmCards = [
    {
      svg: SVGICON.SlrmConnections || SVGICON.UserSvgGreen,
      number: showCount(c.totalSlrmConnections ?? 0),
      title: "Total SLRM Connections",
      changetheme: "success",
    },
    {
      svg: SVGICON.SlrmBin || SVGICON.CardProgress,
      number: showCurrency(c.totalBalanceSlrm ?? 0),
      title: "SLRM Balance",
      changetheme: "primary",
    },
    {
      svg: SVGICON.SlrmDemand || SVGICON.ShiledPrimary,
      number: showCurrency(c.totalSlrmDemandThisMonth ?? 0),
      title: "SLRM Demand - This Month",
      changetheme: "warning",
    },
    {
      svg: SVGICON.SlrmCollected || SVGICON.UserThree,
      number: showCurrency(c.slrmCollectedThisMonth ?? 0),
      title: "SLRM Collected - This Month",
      changetheme: "success",
    },
  ];

const totalCollectedToday = toNumber(
  c.totalTodayReceipts ?? 0
);

const waterToday = toNumber(
  c.waterCollectedToday ?? 0
);

const slrmToday = toNumber(
  c.slrmCollectedToday ?? 0
);

const todayCollected =
  viewType === "water"
    ? waterToday
    : slrmToday;
  return (
    <div className="container-fluid">
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Dashboard</h4>

        <div className="btn-group">
          <button
            className={`btn btn-sm ${
              viewType === "water" ? "btn-primary" : "btn-outline-primary"
            }`}
            onClick={() => setViewType("water")}
          >
            Water
          </button>
          <button
            className={`btn btn-sm ${
              viewType === "slrm" ? "btn-success" : "btn-outline-success"
            }`}
            onClick={() => setViewType("slrm")}
          >
            SLRM
          </button>
        </div>
      </div>

      <div className="row">
        {topCards.map((item, i) => (
          <div className="col-xl-3 col-sm-6" key={`top-${i}`}>
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div
                    className={`icon-box icon-box-lg rounded-circle bg-${item.changetheme}-light`}
                  >
                    {item.svg}
                  </div>

                  <div className="text-end">
                    <h3
                      className={`count text-${item.changetheme} mb-1`}
                      style={{
                        fontSize: "28px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "220px",
                      }}
                      title={item.number}
                    >
                      {item.number}
                    </h3>
                    <span>{item.title}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {(viewType === "water" ? waterCards : slrmCards).map((item, i) => (
          <div className="col-xl-3 col-sm-6" key={`metric-${i}`}>
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div
                    className={`icon-box icon-box-lg rounded-circle bg-${item.changetheme}-light`}
                  >
                    {item.svg}
                  </div>

                  <div className="text-end">
                    <h3
                      className={`count text-${item.changetheme} mb-1`}
                      style={{
                        fontSize: "28px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "220px",
                      }}
                      title={item.number}
                    >
                      {item.number}
                    </h3>
                    <span>{item.title}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row mt-3">
        <div className="col-xl-4 col-lg-5">
          <div className="card">
            <div className="card-header border-0">
              <h4 className="heading mb-0">Today's Collection</h4>
            </div>

            <div className="card-body py-0">
   <SocialNetworkRadialChart
  key={`${viewType}-${todayCollected}`}
  value={todayCollected}
/>
<div className="text-center mt-2">
  <h5 className="mb-0">
    {viewType === "water"
      ? "Water Collected Today"
      : "SLRM Collected Today"}
  </h5>

  <h4 className="mb-0">
    {loading ? "..." : formatCurrency(todayCollected)}
  </h4>
</div>
            </div>
          </div>
        </div>

        <div className="col-xl-8 col-lg-7">
          <ProjectOverviewTab
            height={350}
            dashboardChart={data?.chart}
            viewType={viewType}
          />
        </div>

        <div className="col-xl-6">
          <ActiveProjects
            title={
              viewType === "slrm"
                ? "Today's SLRM Receipts"
                : "Today's Water Receipts"
            }
            rows={data?.todayReceipts || []}
            loading={loading}
            viewType={viewType}
          />
        </div>

        <div className="col-xl-6">
          <RevenueHistoryTable
            rows={viewType === "water" ? waterBills : slrmBills}
            loading={loading}
            viewType={viewType}
          />
        </div>
      </div>
    </div>
  );
};

export default Dashboard3;